<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl">Payments</h1>
    <Button variant="outline" title="Download CSV" :disabled="allPayments.length === 0" @click="exportToCSV">
      <Download class="h-4 w-4" />
      Download
    </Button>
  </div>
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <PaymentsControls
      @update:status="onStatusChange"
      @update:type="onTypeChange"
      @update:method="onMethodChange"
      @update:dateRange="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="paymentsRegisterColumns"
      :data="paginatedPayments"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
    />
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import type { NewPayment, PaymentMethod, PaymentParams, PaymentStatus, PaymentType } from '@/models/payments';
import { LAYUP_API_GET_ITEMS_LIMIT } from '@/services/layupApi';
import { fetchPayments } from '@/services/payments';
import { currency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import { Download } from 'lucide-vue-next';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref } from 'vue';
import DataTable from '../../DataTable.vue';
import PaymentsControls from '../payments-controls/PaymentsControls.vue';
import { paymentsRegisterColumns } from './PaymentTableColumns';

const isLoading = ref(false);
const paymentParams = ref<PaymentParams>({
  state: 'all',
  limit: LAYUP_API_GET_ITEMS_LIMIT,
  sort: '-due',
  skip: 0,
});
const previousParams = ref<PaymentParams>({ ...paymentParams.value });
const allPayments = ref<NewPayment[]>([]);
const allPaymentExports = ref<Partial<NewPayment>[]>([]);

const paginatedPayments = computed(() => {
  return allPayments.value.slice((pageNumber.value - 1) * pageSize.value, pageNumber.value * pageSize.value);
});

const totalPages = ref(1);
const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

onMounted(async () => onUpdateFilter({ ...paymentParams.value }, true));

const onUpdateFilter = async (params: PaymentParams, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  allPayments.value = [];
  allPaymentExports.value = [];
  isLoading.value = true;

  try {
    const response = await fetchPayments(params);

    allPayments.value =
      response.payments?.map((p: any) => ({
        ...p,
        fee: currency(p.fee),
        amount: currency(p.amount),
      })) ?? [];
    allPaymentExports.value = response.export ?? [];

    totalPages.value = Math.ceil(allPayments.value.length / pageSize.value);
    totalCount.value = allPayments.value.length;
    pageNumber.value = 1;

    addMetric('payment-register-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('payment-register-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('payment-register-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: PaymentStatus) => {
  paymentParams.value.state = value;
  // Need to clone the object to avoid mutating the original
  onUpdateFilter({ ...paymentParams.value });
};

const onTypeChange = (value: PaymentType) => {
  paymentParams.value.type = value === 'ALL' ? '' : value;
  onUpdateFilter({ ...paymentParams.value });
};

const onMethodChange = (value: PaymentMethod) => {
  paymentParams.value.method = value === 'ALL' ? '' : value;
  onUpdateFilter({ ...paymentParams.value });
};

const onDateRangeChange = (value: DateRange): void => {
  paymentParams.value.from = new Date(`${value.start?.year}-${value.start?.month}-${value.start?.day}`);
  paymentParams.value.to = new Date(`${value.end?.year}-${value.end?.month}-${value.end?.day}`);
  onUpdateFilter({ ...paymentParams.value });
};

const onClearFilters = (): void => {
  paymentParams.value = {
    state: 'all',
    limit: LAYUP_API_GET_ITEMS_LIMIT,
    sort: '-due',
    skip: 0,
  };
  onUpdateFilter({ ...paymentParams.value });
};

const onSearchTermChange = (value: string): void => {
  paymentParams.value.search = value;
  onUpdateFilter({ ...paymentParams.value });
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};

const exportToCSV = () => {
  if (allPaymentExports.value.length === 0) {
    console.warn('No data available for export');
    return;
  }

  const csvContent = generateCSV(allPaymentExports.value);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `payments-${dayjs().format('YYYY-MM-DD')}.csv`);
  link.click();

  URL.revokeObjectURL(url);
};

const generateCSV = (data: Partial<NewPayment>[]): string => {
  const headerMap: Partial<Record<keyof NewPayment, string>> = {
    client: 'Customer Name',
    amount: 'Amount',
    createdAt: 'Date Created',
    verifiedAt: 'Date Paid',
    dateRefunded: 'Date Refunded',
    due: 'Due Date',
    fee: 'Fee',
    locked: 'Locked',
    orderName: 'Order Name',
    orderReference: 'Order Reference',
    paymentStatus: 'Payment Status',
    paymentType: 'Payment Type',
    settlementBatch: 'Settlement Batch',
    state: 'Status',
    updatedAt: 'Date Updated',
    datePaid: 'Date Paid',
  };

  const headers = Object.keys(headerMap) as Array<keyof NewPayment>;
  const headerTitles = headers.map((header) => headerMap[header] || header);
  const rows = data.map((row) =>
    headers.map((header) => `"${String(row[header] || '').replace(/"/g, '""')}"`).join(','),
  );

  return [headerTitles.join(','), ...rows].join('\n');
};
</script>
